// appfolioRefresh — what the lease builder does with an AppFolio refresh result
// (WEL-169). Pure, so the rules are testable without a browser:
//
//   - changed          → reload the group, then say "Updated from AppFolio just now."
//   - refreshed, same  → nothing to reload; the manual button says it is current,
//                        the background run stays silent
//   - not refreshed    → the manual button shows the server's plain reason; the
//                        background run stays silent (nobody asked for it)
//
// Staff-facing copy only, and never an id: the server's messages are plain words
// and these constants are too.

import type { AppFolioRefreshResult } from './leaseDraftsClient';

export type AppFolioRefreshMode = 'background' | 'manual';

export const REFRESH_UPDATED_NOTE = 'Updated from AppFolio just now.';
export const REFRESH_CURRENT_NOTE = 'Already up to date with AppFolio.';
export const REFRESH_FAILED_FALLBACK = 'The AppFolio refresh didn’t finish. Try again in a minute.';

/**
 * The background refresh runs ONCE per draft, as soon as the draft has loaded:
 * due when there is a draft, it is ready, and this draft has not had its run.
 */
export function backgroundRefreshDue(
  groupId: string | undefined,
  enabled: boolean,
  lastRanFor: string | null,
): boolean {
  return !!groupId && enabled && lastRanFor !== groupId;
}

export interface AppFolioRefreshOutcome {
  note: string | null;
  error: string | null;
  /** True when the group was reloaded. */
  reloaded: boolean;
}

export async function runAppFolioRefresh(opts: {
  call: () => Promise<AppFolioRefreshResult>;
  /** Reload the group (never the form) — only called when something changed. */
  onChanged: () => Promise<unknown> | unknown;
  mode: AppFolioRefreshMode;
}): Promise<AppFolioRefreshOutcome> {
  const manual = opts.mode === 'manual';
  let result: AppFolioRefreshResult;
  try {
    result = await opts.call();
  } catch {
    return { note: null, error: manual ? REFRESH_FAILED_FALLBACK : null, reloaded: false };
  }
  if (result.refreshed && result.changed) {
    await opts.onChanged();
    return { note: REFRESH_UPDATED_NOTE, error: null, reloaded: true };
  }
  if (result.refreshed) {
    return { note: manual ? REFRESH_CURRENT_NOTE : null, error: null, reloaded: false };
  }
  return {
    note: null,
    error: manual ? result.message?.trim() || REFRESH_FAILED_FALLBACK : null,
    reloaded: false,
  };
}
